import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { FlaskConical, Play, TrendingDown, Trophy } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import {
  EmptyState,
  ErrorState,
  LoadingState,
  RankedBars,
  StatTile,
} from '../components/DataViz'
import { fetchFleetScorecards, fetchSkillValidation, sendJson } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useAsync } from '../lib/useAsync'

/**
 * Evaluations (AFOS-41): is the factory getting better?
 * - Skill validation suite: every phase skill's static floor and scenarios.
 * - Squad quality: scorecard grades, ranking, promotion candidates, regressions.
 * The desk's live resolution evaluation lab is not ported yet (AFOS-17) and is
 * named as such instead of shown with placeholder numbers.
 */
export function EvaluationsPage() {
  const { user, loading } = useAuth()
  const suite = useAsync(fetchSkillValidation, !!user)
  const fleet = useAsync(fetchFleetScorecards, !!user)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  if (!loading && !user) return <Navigate to="/signin" replace />
  const perms = new Set(user?.permissions ?? [])
  const canRun = !!user?.is_super_admin || perms.has('agents.manage')

  async function runSuite() {
    setRunning(true)
    setMessage(null)
    try {
      const out = await sendJson<{ skills: number; passed: number }>(
        '/api/afos360/v1/skill-validation/run',
        'POST',
        {},
        'Validation run failed',
      )
      setMessage(`Validated ${out.skills} skills: ${out.passed} passed.`)
      await suite.reload()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Validation run failed')
    } finally {
      setRunning(false)
    }
  }

  const rows = suite.data?.skills ?? []
  const ran = rows.filter((r) => r.last_passed != null)
  const passing = rows.filter((r) => r.last_passed && !r.stale)
  const passRate = ran.length ? Math.round((passing.length / rows.length) * 100) : null
  const statusBars = [
    { key: 'pass', label: 'Passing, current', value: passing.length },
    { key: 'stale', label: 'Changed since last run', value: rows.filter((r) => r.stale && r.last_passed != null).length },
    { key: 'fail', label: 'Failing', value: rows.filter((r) => r.last_passed === false && !r.stale).length },
    { key: 'never', label: 'Never run', value: rows.filter((r) => r.last_passed == null).length },
  ].filter((b) => b.value > 0)

  const totals = fleet.data?.totals
  const gradeBars = Object.entries(totals?.grades ?? {}).map(([g, n]) => ({
    key: g,
    label: `Grade ${g}`,
    value: n,
    href: '/squads',
  }))

  return (
    <AppChrome>
      <main className="afos-page space-y-8">
        <header>
          <h1 className="afos-page-title">Evaluations</h1>
          <p className="afos-page-lead">
            Evidence that the factory is improving: every phase skill validated against its manifest
            and scenarios, and every squad scored on the same eight dimensions.
          </p>
        </header>

        {message && (
          <div role="status" className="rounded-2xl border border-[var(--color-line)] bg-black/25 px-4 py-3 text-sm">
            {message}
          </div>
        )}

        <section aria-labelledby="suite" className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 id="suite" className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Skill validation suite
            </h2>
            {canRun && (
              <button
                type="button"
                disabled={running}
                onClick={() => void runSuite()}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--color-cyan)] px-4 py-2 text-sm font-semibold text-[var(--color-void)] disabled:opacity-50"
              >
                <Play aria-hidden="true" className="h-4 w-4" />
                {running ? 'Running…' : 'Run the suite'}
              </button>
            )}
          </div>
          {suite.error && !suite.data && <ErrorState message={suite.error} onRetry={() => void suite.reload()} />}
          {!suite.data && !suite.error && <LoadingState label="Loading validation results" />}
          {suite.data && (
            <div className="grid gap-4 lg:grid-cols-3">
              <StatTile
                emphasis
                label="Pass rate (current content)"
                value={passRate == null ? '—' : `${passRate}%`}
                hint={passRate == null ? 'The suite has not run yet.' : `${passing.length} of ${rows.length} phase skills`}
                icon={<FlaskConical aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
              />
              <StatTile
                label="Skills with a manifest"
                value={`${suite.data.with_manifest}/${suite.data.total}`}
                hint="Declared capabilities and prohibitions"
              />
              <RankedBars
                title="Skills by validation state"
                data={statusBars}
                empty="No phase skills found."
              />
            </div>
          )}
        </section>

        <section aria-labelledby="squads-quality" className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 id="squads-quality" className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Squad quality
            </h2>
            <Link to="/squads" className="text-sm text-[var(--color-cyan)] hover:underline">
              Squad board →
            </Link>
          </div>
          {fleet.error && !fleet.data && <ErrorState message={fleet.error} onRetry={() => void fleet.reload()} />}
          {!fleet.data && !fleet.error && <LoadingState label="Loading squad scorecards" />}
          {fleet.data && totals && (
            <>
              {!fleet.data.page.complete && (
                <p role="status" className="text-sm text-[var(--color-mist)]">
                  Scored {totals.squads} of {fleet.data.page.total_squads} squads across{' '}
                  {fleet.data.pages} page{fleet.data.pages === 1 ? '' : 's'}
                  {fleet.data.deferred
                    ? `; ${fleet.data.deferred} deferred by the scoring budget are being folded and will count on refresh`
                    : ''}
                  . Grades, candidates, and regressions below cover the scored squads only.
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-3">
                <StatTile label="Squads graded" value={`${totals.graded}/${totals.squads}`} />
                <StatTile
                  label="Promotion candidates"
                  value={totals.promotion_candidates.length}
                  hint="Advisory; a person promotes"
                  icon={<Trophy aria-hidden="true" className="h-5 w-5 text-[var(--color-amber)]" />}
                />
                <StatTile
                  label="Regressed vs baseline"
                  value={totals.regressed.length}
                  icon={<TrendingDown aria-hidden="true" className="h-5 w-5 text-[var(--color-rose)]" />}
                />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <RankedBars
                  title="Squads by grade"
                  data={gradeBars}
                  empty="No squad has a scorecard yet. Scores appear once squads record activity."
                />
                <figure className="glass rounded-3xl p-5">
                  <figcaption className="mb-3 text-sm font-semibold text-[var(--color-foam)]">
                    Ranking
                  </figcaption>
                  {fleet.data.ranking.length === 0 ? (
                    <EmptyState compact>No squads yet.</EmptyState>
                  ) : (
                    <ol className="space-y-1 text-sm">
                      {fleet.data.ranking.slice(0, 10).map((r, i) => (
                        <li key={r.team_id} className="flex items-center justify-between gap-3 rounded-xl px-2 py-1.5 hover:bg-white/5">
                          <span className="min-w-0 truncate text-[var(--color-foam)]">
                            <span className="mr-2 tabular-nums text-[var(--color-mist)]">{i + 1}.</span>
                            {r.name || r.team_id}
                            {r.tier ? <span className="ml-2 text-xs text-[var(--color-mist)]">{r.tier}</span> : null}
                          </span>
                          <span className="tabular-nums text-[var(--color-mist)]">
                            {r.score != null ? Number(r.score).toFixed(1) : 'ungraded'} {r.grade ? `· ${r.grade}` : ''}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </figure>
              </div>
            </>
          )}
        </section>

        <section className="glass rounded-3xl p-5 text-sm text-[var(--color-mist)]">
          <h2 className="mb-1 text-base font-semibold text-[var(--color-foam)]">Resolution evaluation lab</h2>
          The Support Desk's golden-ticket suite and human resolution grading still run in the desk
          engine (<span className="font-mono text-xs">python -m desk evaluate</span>) and move into AFOS
          with the learning port (AFOS-17). Their results are not shown here until then.
        </section>
      </main>
    </AppChrome>
  )
}
